<template>
  <aside
    class="h-full bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 transition-all duration-300 shadow-2xl border-r border-slate-700/50 backdrop-blur-lg flex-shrink-0"
    :class="getSidebarClasses()"
  >
    <!-- Mobile Close Button -->
    <div v-if="props.isMobileView && isExpanded" class="flex justify-between items-center p-6 border-b border-slate-700/50 lg:hidden bg-slate-800/80 backdrop-blur-sm">
      <h2 class="text-xl font-bold text-white tracking-wide">Navigation</h2>
      <button
        @click="closeSidebar"
        class="p-3 rounded-xl hover:bg-slate-700/50 transition-all duration-200 group"
        aria-label="Close menu"
      >
        <i class="pi pi-times text-xl text-gray-300 group-hover:text-white transition-colors"></i>
      </button>
    </div>

    <!-- Logo Section -->
    <div v-if="isExpanded && !props.isMobileView" class="flex justify-center py-8 border-b border-slate-700/50 bg-slate-800/30">
      <div class="relative">
        <img
          :src="logoURL"
          alt="Logo"
          class="h-32 lg:h-40 w-auto object-contain filter drop-shadow-lg"
        />
        <div class="absolute inset-0 bg-gradient-to-t from-transparent to-white/5 rounded-lg"></div>
      </div>
    </div>

    <!-- Empty space when collapsed -->
    <div v-if="!isExpanded && !props.isMobileView" class="py-8 border-b border-slate-700/50"></div>

    <!-- Menu Items -->
    <div class="mt-1 pb-24 overflow-y-auto custom-scrollbar" :class="getMenuContainerClasses()">

      <!-- Home -->
      <router-link to="/dashboard" class="sidebar-link group" @click="handleMobileNavigation">
        <div class="sidebar-icon-container">
          <i class="pi pi-home" :class="getIconClasses()"></i>
        </div>
        <span v-show="isExpanded" :class="getTextClasses()">Dashboard</span>
      </router-link>

      <router-link v-if="authStore.roles.includes('viewSchool')" to="/school" class="sidebar-link group" @click="handleMobileNavigation">
        <div class="sidebar-icon-container">
          <i class="pi pi-building" :class="getIconClasses()"></i>
        </div>
        <span v-show="isExpanded" :class="getTextClasses()">School Management</span>
      </router-link>

      <div class="border-t border-slate-600/50 mt-3 pt-3">
        <div v-show="isExpanded" class="px-4 py-2 text-xs uppercase tracking-wider text-slate-400 font-bold">
          Education
        </div>

        <router-link v-if="authStore.roles.includes('viewRoom')" to="/classroom/classroomlist" class="sidebar-link group" @click="handleMobileNavigation">
          <div class="sidebar-icon-container">
            <i class="pi pi-desktop" :class="getIconClasses()"></i>
          </div>
          <span v-show="isExpanded" :class="getDropdownTextClasses()">Classrooms</span>
        </router-link>

        <router-link v-if="authStore.roles.includes('viewClasses')" to="/classeslist" class="sidebar-link group" @click="handleMobileNavigation">
          <div class="sidebar-icon-container">
            <i class="pi pi-table" :class="getIconClasses()"></i>
          </div>
          <span v-show="isExpanded" :class="getDropdownTextClasses()">Classes</span>
        </router-link>

        <router-link v-if="authStore.roles.includes('viewSchoolYear')" to="/schoolyear" class="sidebar-link group" @click="handleMobileNavigation">
          <div class="sidebar-icon-container">
            <i class="pi pi-calendar-plus" :class="getIconClasses()"></i>
          </div>
          <span v-show="isExpanded" :class="getDropdownTextClasses()">Academic Year</span>
        </router-link>
      </div>

      <div class="border-t border-slate-600/50 mt-3 pt-3">
        <div v-show="isExpanded" class="px-4 py-2 text-xs uppercase tracking-wider text-slate-400 font-bold">
          Members
        </div>

        <router-link v-if="authStore.roles.includes('viewStaff')" to="/staff" class="sidebar-link group" @click="handleMobileNavigation">
          <div class="sidebar-icon-container">
            <i class="pi pi-user-edit" :class="getIconClasses()"></i>
          </div>
          <span v-show="isExpanded" :class="getDropdownTextClasses()">Staff Members</span>
        </router-link>

        <router-link v-if="authStore.roles.includes('viewParents')" to="/parent" class="sidebar-link group" @click="handleMobileNavigation">
          <div class="sidebar-icon-container">
            <i class="pi pi-user" :class="getIconClasses()"></i>
          </div>
          <span v-show="isExpanded" :class="getDropdownTextClasses()">Parents</span>
        </router-link>

        <router-link v-if="authStore.roles.includes('viewStudents')" to="/student/studentlist" class="sidebar-link group" @click="handleMobileNavigation">
          <div class="sidebar-icon-container">
            <i class="pi pi-graduation-cap" :class="getIconClasses()"></i>
          </div>
          <span v-show="isExpanded" :class="getDropdownTextClasses()">Students</span>
        </router-link>
      </div>

      <router-link v-if="authStore.roles.includes('viewSdoOfficers')" to="/tree" class="sidebar-link group" @click="handleMobileNavigation">
        <div class="sidebar-icon-container">
          <i class="pi pi-sitemap" :class="getIconClasses()"></i>
        </div>
        <span v-show="isExpanded" :class="getTextClasses()">SDO Officers</span>
      </router-link>

      <div class="border-t border-slate-600/50 mt-3 pt-3">
        <div v-show="isExpanded" class="px-4 py-2 text-xs uppercase tracking-wider text-slate-400 font-bold">
          Communication
        </div>

        <router-link v-if="authStore.roles.includes('viewChatbox')" to="/chat" class="sidebar-link group" @click="handleMobileNavigation">
          <div class="sidebar-icon-container">
            <i class="pi pi-comment" :class="getIconClasses()"></i>
          </div>
          <span v-show="isExpanded" :class="getDropdownTextClasses()">Chat System</span>
        </router-link>

        <router-link v-if="authStore.roles.includes('viewUserChat')" to="/userchat" class="sidebar-link group" @click="handleMobileNavigation">
          <div class="sidebar-icon-container">
            <i class="pi pi-comments" :class="getIconClasses()"></i>
          </div>
          <span v-show="isExpanded" :class="getDropdownTextClasses()">User Messages</span>
        </router-link>
      </div>

      <div class="border-t border-slate-600/50 mt-3 pt-3">
        <div v-show="isExpanded" class="px-4 py-2 text-xs uppercase tracking-wider text-slate-400 font-bold">
          Management
        </div>

        <router-link v-if="authStore.roles.includes('viewEvents')" to="/calendar" class="sidebar-link group" @click="handleMobileNavigation">
          <div class="sidebar-icon-container">
            <i class="pi pi-calendar" :class="getIconClasses()"></i>
          </div>
          <span v-show="isExpanded" :class="getDropdownTextClasses()">Calendar</span>
        </router-link>

        <router-link v-if="authStore.roles.includes('viewReports')" to="/adminreport" class="sidebar-link group" @click="handleMobileNavigation">
          <div class="sidebar-icon-container">
            <i class="pi pi-chart-bar" :class="getIconClasses()"></i>
          </div>
          <span v-show="isExpanded" :class="getDropdownTextClasses()">Reports & Analytics</span>
        </router-link>

        <router-link v-if="authStore.roles.includes('viewSchedule')" to="/sched" class="sidebar-link group" @click="handleMobileNavigation">
          <div class="sidebar-icon-container">
            <i class="pi pi-clock" :class="getIconClasses()"></i>
          </div>
          <span v-show="isExpanded" :class="getDropdownTextClasses()">Schedule Management</span>
        </router-link>

        <router-link v-if="authStore.roles.includes('viewOfficeHours')" to="/office" class="sidebar-link group" @click="handleMobileNavigation">
          <div class="sidebar-icon-container">
            <i class="pi pi-briefcase" :class="getIconClasses()"></i>
          </div>
          <span v-show="isExpanded" :class="getDropdownTextClasses()">Office Hours</span>
        </router-link>

        <router-link v-if="authStore.roles.includes('viewExtraCurriculars')" to="/extra" class="sidebar-link group" @click="handleMobileNavigation">
          <div class="sidebar-icon-container">
            <i class="pi pi-star" :class="getIconClasses()"></i>
          </div>
          <span v-show="isExpanded" :class="getDropdownTextClasses()">Extra Curricular</span>
        </router-link>
      </div>

      <div class="border-t border-slate-600/50 mt-3 pt-3">
        <div v-show="isExpanded" class="px-4 py-2 text-xs uppercase tracking-wider text-slate-400 font-bold">
          System Settings
        </div>

        <router-link v-if="authStore.roles.includes('viewDropdownManager')" to="/needs" class="sidebar-link group" @click="handleMobileNavigation">
          <div class="sidebar-icon-container">
            <i class="pi pi-box" :class="getIconClasses()"></i>
          </div>
          <span v-show="isExpanded" :class="getDropdownTextClasses()">Dropdown Manager</span>
        </router-link>

        <router-link v-if="authStore.roles.includes('viewRolesRights')" to="/rights" class="sidebar-link group" @click="handleMobileNavigation">
          <div class="sidebar-icon-container">
            <i class="pi pi-lock" :class="getIconClasses()"></i>
          </div>
          <span v-show="isExpanded" :class="getDropdownTextClasses()">Roles & Permissions</span>
        </router-link>
      </div>

      <router-link to="/security" class="sidebar-link group" @click="handleMobileNavigation">
        <div class="sidebar-icon-container">
          <i class="pi pi-shield" :class="getIconClasses()"></i>
        </div>
        <span v-show="isExpanded" :class="getDropdownTextClasses()">Security Center</span>
      </router-link>

      <router-link to="/batchupload" class="sidebar-link group" @click="handleMobileNavigation">
        <div class="sidebar-icon-container">
          <i class="pi pi-clipboard" :class="getIconClasses()"></i>
        </div>
        <span v-show="isExpanded" :class="getDropdownTextClasses()">Batch Upload</span>
      </router-link>
    </div>

    <!-- Logout Section -->
    <div class="absolute bottom-0 left-0 right-0 border-t border-slate-700/50 bg-gradient-to-t from-slate-900 to-slate-800/50 backdrop-blur-sm">
      <button @click="logout" class="sidebar-link w-full text-left group hover:bg-red-500/10">
        <div class="sidebar-icon-container group-hover:bg-red-500/20">
          <i class="pi pi-sign-out" :class="getIconClasses() + ' group-hover:text-red-400'"></i>
        </div>
        <span v-show="isExpanded" :class="getTextClasses() + ' group-hover:text-red-400'">Sign Out</span>
      </button>
    </div>
  </aside>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/auth/auth';
import logoURL from '@/assets/sanlogo.png';

const router = useRouter();
const authStore = useAuthStore();

const props = defineProps({
  isMobileView: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['sidebar-toggle']);

const isExpanded = ref(false);

const getTextClasses = () => {
  return props.isMobileView
    ? 'text-lg font-semibold tracking-wide'
    : 'text-sm font-medium tracking-wide';
};

const getIconClasses = () => {
  return props.isMobileView
    ? 'text-2xl p-3'
    : 'text-lg p-3';
};

const getDropdownTextClasses = () => {
  return props.isMobileView
    ? 'text-base font-medium tracking-wide'
    : 'text-sm font-medium tracking-wide';
};

const getMenuContainerClasses = () => {
  return props.isMobileView
    ? 'h-[calc(100%-16rem)]'
    : 'h-[calc(100%-12rem)] lg:h-[calc(100%-14rem)]';
};

const getSidebarClasses = () => {
  const baseClasses = [];

  if (props.isMobileView) {
    baseClasses.push('fixed', 'top-0', 'left-0', 'z-50', 'h-screen');
    if (isExpanded.value) {
      baseClasses.push('w-full');
    } else {
      baseClasses.push('w-0', 'overflow-hidden');
    }
  } else {
    baseClasses.push(isExpanded.value ? 'w-72' : 'w-20');
  }

  return baseClasses.join(' ');
};

const toggleSidebar = () => {
  isExpanded.value = !isExpanded.value;

  if (!props.isMobileView) {
    localStorage.setItem('sidebarExpanded', isExpanded.value.toString());
  }

  emit('sidebar-toggle', isExpanded.value);
};

const closeSidebar = () => {
  if (props.isMobileView) {
    isExpanded.value = false;
    emit('sidebar-toggle', false);
  }
};

const handleMobileNavigation = () => {
  if (props.isMobileView) {
    setTimeout(() => {
      closeSidebar();
    }, 100);
  }
};

const logout = () => {
  authStore.logout();
  router.push('/auth/login');
};

watch(() => props.isMobileView, (newValue) => {
  if (newValue) {
    isExpanded.value = false;
  } else {
    const savedState = localStorage.getItem('sidebarExpanded');
    if (savedState !== null) {
      isExpanded.value = savedState === 'true';
    } else {
      isExpanded.value = true;
    }
  }

  emit('sidebar-toggle', isExpanded.value);
});

onMounted(() => {
  if (!props.isMobileView) {
    const savedState = localStorage.getItem('sidebarExpanded');
    if (savedState !== null) {
      isExpanded.value = savedState === 'true';
    } else {
      isExpanded.value = true;
    }
  }

  emit('sidebar-toggle', isExpanded.value);
});

onUnmounted(() => {
  // Cleanup if needed
});

defineExpose({
  toggleSidebar,
  closeSidebar
});
</script>

<style scoped>
.sidebar-link {
  @apply relative flex items-center gap-4 px-6 py-4 text-slate-300 transition-all duration-300 rounded-none font-medium;
  min-height: 64px;
  border-left: 3px solid transparent;
}

.sidebar-link:hover {
  @apply text-white bg-slate-700/30;
}

.sidebar-link:hover .sidebar-icon-container {
  @apply bg-blue-500/20 text-blue-400;
  transform: scale(1.05);
}

.sidebar-link.router-link-exact-active {
  @apply bg-blue-500/10 text-white;
}

.sidebar-link.router-link-exact-active .sidebar-icon-container {
  @apply bg-blue-500/20 text-blue-400;
}

.sidebar-icon-container {
  @apply flex items-center justify-center w-10 h-10 rounded-xl bg-slate-700/50 transition-all duration-300;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: rgba(51, 65, 85, 0.3);
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(100, 116, 139, 0.6);
  border-radius: 3px;
  border: 1px solid rgba(71, 85, 105, 0.3);
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(148, 163, 184, 0.8);
}

.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(100, 116, 139, 0.6) rgba(51, 65, 85, 0.3);
}

@media (max-width: 1023px) {
  .sidebar-link {
    @apply px-6 py-5;
    min-height: 64px;
  }

  aside.w-full {
    background: linear-gradient(135deg, rgba(30, 41, 59, 0.98), rgba(15, 23, 42, 0.95));
    backdrop-filter: blur(20px);
  }
}

.group:hover .sidebar-icon-container {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.sidebar-link:focus {
  @apply outline-none ring-2 ring-blue-500/50 ring-inset;
}

button.sidebar-link:active {
  transform: scale(0.98);
}
</style>
